/** @jsxImportSource @emotion/react */

import { useAtom } from 'jotai';
import useNotifications from '../../shared/hooks/use-notifications';
import { getUserChannelData, shouldHideDTDetails } from '../login/v1/utility';
import useAddonPurchase from '../../shared/hooks/use-addon-purchase';
import { aiBotUpgradeModalOpenAtom } from './state';
import { useCallback } from 'react';
import { openPopup } from '../../shared/utils/popup';
import QSModal from '../../shared/components/molecules/modal';
import { css } from '@emotion/react';
import { QSButton, QSIconButton, QSTypography } from '../../shared/components/atoms';
import ProgressiveImage from '../../shared/components/atoms/progressive-image';
import { PlanAddonTypes } from '../renew-plan/v1/typings';
import { Close } from '@mui/icons-material';
import { ReactComponent as AIBotIcon } from './icons/AiBotIcon.svg';
import { ReactComponent as AIBotQuestion } from './icons/AibotQuestion.svg';
import { ReactComponent as AIBotAnalytics } from './icons/AiBotAnalytics.svg';
import { ReactComponent as AIBotFlow } from './icons/AiBotFlow.svg';
import { ReactComponent as AIBotShield } from './icons/AiBotShield.svg';
import { ReactComponent as AIBotChatGPT } from './icons/AiBotChatGPT.svg';
import AIBotBook from './images/AiBotBook.png';
import Loader from '../../shared/components/molecules/loader';
import PlayCircle from '../../shared/icons/PlayCircle';
import AIBotImage from './images/AI-bot-png.png';

const FEATURES = [
  {
    featureLabel: 'Handle multiple intents in a single conversation',
    icon: <AIBotQuestion />,
  },
  {
    featureLabel: 'Train your bot on frequently asked questions',
    img: AIBotBook,
  },
  {
    featureLabel: 'Monitor AI bot performance with analytics',
    icon: <AIBotAnalytics />,
  },
  {
    featureLabel: 'Trigger bot flow when an intent is detected',
    icon: <AIBotFlow />,
  },
  {
    featureLabel: 'Fallback setup for queries bot cannot answer',
    icon: <AIBotShield />,
  },
  {
    featureLabel: 'Choose between Chat GPT 4o or 4o mini model',
    icon: <AIBotChatGPT />,
  },
];

export default function AIBotUpgradeModal() {
  const [upgradeModal, setUpgradeModal] = useAtom(aiBotUpgradeModalOpenAtom);
  const { addNotification } = useNotifications();
  const channelData = getUserChannelData();
  const hideDTDetails = shouldHideDTDetails();
  const { purchaseAddon, loading: purchaseLoading } = useAddonPurchase();
  let contactUsLink = `https://wa.me/${channelData?.supportNumber}?text=I%27m%20interested%20in%20buying%20AI%20Bot%20Addon`;

  const onClose = useCallback(() => {
    setUpgradeModal(false);
  }, [setUpgradeModal]);

  const onClickContactUs = useCallback(async () => {
    try {
      if (hideDTDetails) {
        openPopup(contactUsLink, '_blank');
      } else {
        await purchaseAddon(PlanAddonTypes.AiBot);
      }
      onClose();
    } catch (error) {
      addNotification({
        message: (error as Error).message ?? 'An error occurred. Please try again',
        type: 'error',
      });
    }
  }, [addNotification, onClose]);

  if (!upgradeModal) {
    return null;
  }

  const renderCTA = () => {
    if(hideDTDetails){
      return 'Contact Us';
    }
    return (
      <>
        {purchaseLoading ? (
          <Loader
            size={16}
            loaderProps={{
              sx: {
                color: 'white',
              },
            }}
          />
        ) : (
          'Purchase Now'
        )}
      </>
    )
  };

  return (
    <QSModal
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
      `}
      open={upgradeModal}
      onClose={onClose}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          border-radius: 8px;
          overflow: hidden;
          background: white;
          outline: none;
          position: relative;
          padding: 16px;
          max-width: 730px;
        `}
      >
        <QSIconButton
          css={css`
            position: absolute;
            top: 8px;
            right: 8px;
          `}
          sx={{}}
          onClick={onClose}
        >
          <Close />
        </QSIconButton>
        <div
          css={css`
            flex: 1;
            overflow: auto;
            display: flex;
            align-items: flex-start;
            flex-wrap: wrap;
          `}
        >
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              flex: 1;
              background-color: #f6f1e7;
              border-radius: 8px;
              overflow: hidden;
              max-width: 300px;
            `}
          >
            <ProgressiveImage 
              imgProps={{ width: '100%', height: '420px' }} 
              src={AIBotImage}
            />
          </div>
          <div
            css={css`
              flex: 1;
              padding: 0 12px;
              padding-left: 20px;
            `}
          >
            <span
              css={css`
                border: 1px solid #e0e0e9;
                border-radius: 50px;
                padding: 4px 8px 4px 4px;
                display: inline-flex;
                align-items: center;
                font-size: 12px;
                margin-top: 0;
                font-weight: 500;
                color: #8959E5;
                background-color: #8959E514;
              `}
            >
              <AIBotIcon />
              <span
                css={css`
                  margin-left: 5px;
                `}
              >
                Unlock AI Chatbot Super Powers
              </span>
            </span>
            <QSTypography
              css={css`
                font-weight: 600;
                padding-right: 12px;
                font-size: 20px;
                overflow: hidden;
                line-height: 28px;
                margin-bottom: 16px;
                margin-top: 16px;
              `}
            >
              Let AI handle customer chats <br /><span css={css`color: rgba(45, 45, 45, 0.70);`}>with smart intent management and bot flow integration</span>
            </QSTypography>

            {FEATURES.map((feature, key) => (
              <div
                key={key}
                css={css`
                  display: inline-flex;
                  align-items: center;
                  justify-content: flex-start;
                  margin-bottom: 10px;
                  font-weight: 500;
                  font-size: 14px;
                  gap: 12px;
                `}
              >
                {feature?.icon ? feature?.icon : <img src={feature?.img} alt={feature?.featureLabel} />}
                <span
                  css={css`
                    color: rgba(45, 45, 45, 1);
                  `}
                >
                  {feature?.featureLabel}
                </span>
              </div>
            ))}
            <div
              css={css`
                margin-top: 12px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 8px;
              `}
            >
              <QSButton
                css={css`
                  height: 40px;
                  width: 100%;
                  white-space: nowrap;
                `}
                onClick={onClickContactUs}
              >
                {renderCTA()}
              </QSButton>
            </div>
          </div>
        </div>
      </div>
    </QSModal>
  );
} 